/* stimulusFetch: 'lazy' */
import {Controller} from "@hotwired/stimulus";

const nameRegex = /([a-zA-Z',.-]+( [a-zA-Z',.-]+)*){2,30}/;

const phoneRegex = /^[\d ()+-]+$/;

const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const cityRegex = /([a-zA-Z',.-]+( [a-zA-Z',.-]+)*){2,30}/;

export default class extends Controller {
    static targets = [
        'existingRow',
        'typeExistingInput',
        'typeNewInput',
        'idInput',
        'newRow',
        'nameInput',
        'emailInput',
        'phoneInput',
        'cityInput',
    ];

    hasErrors = false;

    connect() {
        this.nameInputTarget.addEventListener('input', () => this.validateInput(this.nameInputTarget, this.isInvalidNameInput.bind(this), false));
        this.emailInputTarget.addEventListener('input', () => this.validateInput(this.emailInputTarget, this.isInvalidEmailInput.bind(this), false));
        this.phoneInputTarget.addEventListener('input', () => this.validateInput(this.phoneInputTarget, this.isInvalidPhoneInput.bind(this), false));

        this.nameInputTarget.addEventListener('focusout', () => this.validateInput(this.nameInputTarget, this.isInvalidNameInput.bind(this)));
        this.emailInputTarget.addEventListener('focusout', () => this.validateInput(this.emailInputTarget, this.isInvalidEmailInput.bind(this)));
        this.phoneInputTarget.addEventListener('focusout', () => this.validateInput(this.phoneInputTarget, this.isInvalidPhoneInput.bind(this)));
    }

    validateChosenOrCreated() {
        this.clearErrors();

        const type = this.getType();

        if ('existing' === type) {
            if (!this.idInputTarget.value) {
                this.idInputTarget.classList.add('is-invalid');
                this.hasErrors = true;
            }
        } else if ('new' === type) {
            this.validateInput(this.nameInputTarget, this.isInvalidNameInput.bind(this));
            this.validateInput(this.emailInputTarget, this.isInvalidEmailInput.bind(this));
            this.validateInput(this.phoneInputTarget, this.isInvalidPhoneInput.bind(this));
        } else {
            throw new Error('Unknown litigant type: ' + type);
        }

        if (this.hasErrors) {
            return;
        }

        const data = {};

        if ('existing' === type) {
            data['litigant_id'] = this.idInputTarget.value;
        } else if ('new' === type) {
            data['litigant_name'] = this.nameInputTarget.value;
            data['litigant_email'] = this.emailInputTarget.value;
            data['litigant_phone'] = this.phoneInputTarget.value;
            data['litigant_city'] = this.cityInputTarget.value;
        } else {
            throw new Error('Unknown litigant type: ' + type);
        }

        showPreloader();

        $.post(routes['app_litigant_validate'], data, (response) => {
            if (response.status) {
                this.dispatch('litigant-chosen-or-created', {
                    detail: {
                        type: type,
                        id: this.idInputTarget.value,
                        name: this.nameInputTarget.value,
                        email: this.emailInputTarget.value,
                        phone: this.phoneInputTarget.value,
                        city: this.cityInputTarget.value,
                    }
                });
            } else {
                let noCustomErrors = true;

                if (response.message.includes(translations['validators.Client with this email already exists.'])) {
                    noCustomErrors = false;

                    this.setInputIsInvalid(this.emailInputTarget, true, translations['validators.Client with this email already exists.']);
                } else if (response.message.includes(translations['validators.This e-mail address is not valid'])) {
                    noCustomErrors = false;

                    this.setInputIsInvalid(this.emailInputTarget, true, translations['validators.This e-mail address is not valid']);
                }

                if (response.message.includes(translations['validators.This value is not a valid phone number.'])) {
                    noCustomErrors = false;

                    this.setInputIsInvalid(this.phoneInputTarget, true, translations['validators.This value is not a valid phone number.']);
                }

                if (noCustomErrors) {
                    addAlert('danger', response.message || translations['profile.An unknown error occurred.']);
                }
            }
        })
            .fail(() => {
            })
            .always(() => {
                hidePreloader();
            });
    }

    getType() {
        if (this.typeExistingInputTarget.checked) {
            return this.typeExistingInputTarget.value;
        }

        if (this.typeNewInputTarget.checked) {
            return this.typeNewInputTarget.value;
        }

        throw new Error('No litigant type is selected');
    }

    getName() {
        if (this.idInputTarget.value) {
            return this.idInputTarget.tomselect.options[this.idInputTarget.tomselect.getValue()].text
        }

        if (this.nameInputTarget.value) {
            return this.nameInputTarget.value;
        }

        return null
    }

    typeOnChange(event) {
        const type = this.getType();

        if ('new' === type) {
            this.enableNewRow();
            this.disableExistingRow();
            this.clearErrors()
        } else if ('existing' === type) {
            this.clearErrors()
            this.enableExistingRow();
            this.disableNewRow();
        } else {
            throw new Error('Unknown litigant type: ' + type);
        }
    }

    enableInputsAtTarget(target) {
        target.querySelectorAll('input, select').forEach((element) => {
            element.removeAttribute('disabled');

            if ('undefined' !== typeof element.tomselect) {
                element.tomselect.enable();
            }
        });
    }

    disableInputsAtTarget(target) {
        target.querySelectorAll('input, select').forEach((element) => {
            element.setAttribute('disabled', 'disabled');

            if ('undefined' !== typeof element.tomselect) {
                element.tomselect.disable();
            }
        });
    }

    enableNewRow() {
        this.enableInputsAtTarget(this.newRowTarget);
    }

    disableNewRow() {
        this.disableInputsAtTarget(this.newRowTarget);
    }

    enableExistingRow() {
        this.enableInputsAtTarget(this.existingRowTarget);
    }

    disableExistingRow() {
        this.disableInputsAtTarget(this.existingRowTarget);
    }

    clear() {
        this.idInputTarget.value = '';
        this.idInputTarget.tomselect.clear(true);
        this.nameInputTarget.value = '';
        this.emailInputTarget.value = '';
        this.phoneInputTarget.value = '';
        this.cityInputTarget.value = '';
        this.cityInputTarget.tomselect.clear(true);

        this.typeExistingInputTarget.checked = true;
        this.typeNewInputTarget.checked = false;

        this.enableExistingRow();
        this.disableNewRow();
    }

    clearErrors() {
        this.hasErrors = false;

        this.setInputIsInvalid(this.nameInputTarget, false);
        this.setInputIsInvalid(this.emailInputTarget, false);
        this.setInputIsInvalid(this.phoneInputTarget, false);
    }

    setInputIsInvalid(input, isInvalid, invalidFeedback = "") {
        const inputController = this.application.getControllerForElementAndIdentifier(
            input,
            'input'
        );

        inputController.isInvalidValue = isInvalid;
        inputController.invalidFeedbackValue = invalidFeedback;
    }

    validateInput(input, isInvalidFunction, showInvalidFeedback = true) {
        const {isInvalid, invalidFeedback} = isInvalidFunction();

        if (isInvalid) {
            this.hasErrors = true;

            if (showInvalidFeedback) {
                this.setInputIsInvalid(input, true, invalidFeedback);
            }
        } else {
            this.setInputIsInvalid(input, false);
        }
    }

    isInvalidNameInput() {
        const isInvalid = !this.nameInputTarget.value || this.nameInputTarget.value.length < 3 || !nameRegex.test(this.nameInputTarget.value);
        const invalidFeedback = translations['account.Please complete this field'];

        return {isInvalid, invalidFeedback};
    }

    isInvalidEmailInput() {
        const isInvalid = !this.emailInputTarget.value || !emailRegex.test(this.emailInputTarget.value);
        const invalidFeedback = translations['validators.This e-mail address is not valid'];

        return {isInvalid, invalidFeedback};
    }

    isInvalidPhoneInput() {
        const isInvalid = !this.phoneInputTarget.value || !phoneRegex.test(this.phoneInputTarget.value);
        const invalidFeedback = translations['account.Please enter a valid number'];

        return {isInvalid, invalidFeedback};
    }

    getData() {
        const data = {};
        const type = this.getType();

        if ('existing' === type) {
            data.litigant_id = this.idInputTarget.value;
        } else if ('new' === type) {
            data.litigant_name = this.nameInputTarget.value;
            data.litigant_email = this.emailInputTarget.value;
            data.litigant_phone = this.phoneInputTarget.value;
            data.litigant_city = this.cityInputTarget.value;
        } else {
            throw new Error('Unknown litigant type: ' + type);
        }

        return data;
    }
}
